import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Printer, Heart, HandHeart, Users, MessageSquare, ShieldCheck,
  CheckCircle2, FileText, Target, ClipboardList, Sparkles, AlertTriangle,
} from 'lucide-react';
import Header from '../components/Header';

const EMERALD = '#047857';
const EMERALD_DEEP = '#064E3B';
const AMBER = '#D97706';
const NAVY = '#0E2D5C';
const SLATE = '#334155';

const VALUES = [
  { n: 1, title: 'Compassion First', body: 'Every patient treated with dignity, empathy, respect. Communication is clear, kind, reassuring.', icon: Heart },
  { n: 2, title: 'Community-Focused Care', body: 'Serve the local community. Build trust, reliability, responsiveness with referrers.', icon: HandHeart },
  { n: 3, title: 'Patient-Centred Decisions', body: 'Ask: "What is safest and best for this patient right now?"', icon: Target },
  { n: 4, title: 'Closed-Loop Care', body: 'No result or procedure complete until communicated, acknowledged, and followed up.', icon: CheckCircle2, critical: true },
  { n: 5, title: 'Accountability Without Blame', body: 'Errors reported, reviewed, learned from. System improvement, not individual blame.', icon: ShieldCheck },
];

const BEHAVIOURS = [
  { who: 'Patients', items: ['Introduce yourself clearly', 'Explain procedures simply', 'Check understanding', 'Provide reassurance', 'Never dismiss concerns'] },
  { who: 'Colleagues', items: ['Communicate respectfully', 'Support each other under pressure', 'Escalate concerns early', 'Avoid blame or confrontation'] },
  { who: 'Referrers', items: ['Respond promptly to urgent findings', 'Provide clear, actionable reports', 'Maintain accessibility'] },
];

const LOOPS = [
  { title: 'Imaging → Report → Communication', body: 'Every study reported · delivered · acknowledged (urgent cases).' },
  { title: 'Procedures → Follow-Up', body: 'Immediate post-procedure care · written instructions · 48-hour follow-up call.' },
  { title: 'Biopsy → Pathology → Correlation', body: 'Track · review pathology · correlate with imaging · communicate to referrer.' },
  { title: 'Urgent Findings', body: 'Communicate immediately · document · escalate if GP unavailable.' },
];

const DECISION = [
  'Is this safe for the patient?',
  'Have I communicated clearly?',
  'Is the loop closed?',
  'Have I documented this?',
];

const EXAMPLES = [
  { title: 'Urgent finding', body: 'Communicate immediately. Do not wait for GP. Escalate to ED if required.', tone: '#DC2626' },
  { title: 'Patient anxiety', body: 'Pause. Reassure. Explain again. Proceed only when comfortable.', tone: '#D97706' },
  { title: 'Missed follow-up', body: 'Identify gap. Contact patient / referrer. Close the loop.', tone: '#2563EB' },
];

export default function WorkCultureSOPPage() {
  const navigate = useNavigate();
  const onPrint = () => window.print();

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body, .min-h-screen { background: white !important; }
          .print-container { padding: 0 !important; max-width: none !important; }
          .avoid-break { break-inside: avoid; page-break-inside: avoid; }
          h1, h2, h3 { color: #0E2D5C !important; }
        }
      `}</style>

      <div className="no-print max-w-5xl mx-auto px-4 sm:px-6 pt-6 flex items-center justify-between">
        <button onClick={() => navigate('/sops')} data-testid="culture-back"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Back to SOPs
        </button>
        <button onClick={onPrint} data-testid="culture-print"
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border"
          style={{ borderColor: `${EMERALD}40`, color: EMERALD }}>
          <Printer className="w-3.5 h-3.5" /> Print / PDF
        </button>
      </div>

      <main className="print-container max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="work-culture-page">
        {/* Cover */}
        <section className="avoid-break rounded-3xl border-2 p-8 mb-8"
          style={{ borderColor: `${EMERALD}33`, background: `linear-gradient(135deg, ${EMERALD}0D 0%, transparent 65%)` }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: EMERALD, color: 'white' }}>
              <Sparkles className="w-7 h-7" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: EMERALD }}>SP Radiology · Culture Charter</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Standard Operating Framework</p>
            </div>
          </div>
          <h1 className="font-['Outfit'] text-3xl sm:text-5xl font-black tracking-tight mb-3" style={{ color: NAVY }}>
            Work Culture &amp; Clinical Philosophy
          </h1>
          <blockquote className="mt-5 pl-5 border-l-4 text-lg italic leading-relaxed" style={{ borderColor: EMERALD, color: SLATE }}>
            The patient is at the centre of every decision. No clinical interaction is complete until the loop is closed.
          </blockquote>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8 text-sm">
            <Meta label="Version" value="1.0" />
            <Meta label="Review cycle" value="Annual" />
            <Meta label="Owner" value="Medical Director" />
            <Meta label="Governance lead" value="Practice Manager" />
          </div>
        </section>

        {/* Purpose */}
        <Section icon={Target} title="1 · Purpose" tone={EMERALD}>
          <ul className="space-y-1.5 text-sm">
            <Bullet>Every patient interaction is compassionate and respectful.</Bullet>
            <Bullet>Clinical care is safe, accountable, and complete.</Bullet>
            <Bullet>Staff operate within a consistent, high-trust environment.</Bullet>
            <Bullet>All work is aligned with a patient-centred, closed-loop model of care.</Bullet>
          </ul>
        </Section>

        {/* Values */}
        <Section icon={Heart} title="2 · Foundational Values" tone={EMERALD}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {VALUES.map(v => (
              <div key={v.n} className={`avoid-break rounded-2xl border p-4 ${v.critical ? 'border-2' : ''}`}
                style={{ borderColor: v.critical ? EMERALD : `${EMERALD}2A`, backgroundColor: v.critical ? `${EMERALD}08` : 'transparent' }}>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                    style={{ backgroundColor: v.critical ? EMERALD : `${EMERALD}15`, color: v.critical ? 'white' : EMERALD }}>
                    <v.icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <h3 className="font-['Outfit'] font-bold text-sm" style={{ color: NAVY }}>{v.n}. {v.title}</h3>
                      {v.critical && <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: EMERALD }}>CRITICAL</span>}
                    </div>
                    <p className="text-xs leading-relaxed" style={{ color: SLATE }}>{v.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Expected behaviours */}
        <Section icon={Users} title="3 · Expected Behaviours" tone={EMERALD}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {BEHAVIOURS.map(b => (
              <div key={b.who} className="avoid-break rounded-2xl border p-4" style={{ borderColor: `${EMERALD}2A` }}>
                <p className="text-[11px] font-black uppercase tracking-[0.15em] mb-2" style={{ color: EMERALD }}>With {b.who}</p>
                <ul className="space-y-1 text-xs">
                  {b.items.map((it, i) => <li key={i} className="flex gap-1.5"><span style={{ color: EMERALD }}>›</span><span style={{ color: SLATE }}>{it}</span></li>)}
                </ul>
              </div>
            ))}
          </div>
        </Section>

        {/* Closed loop */}
        <Section icon={CheckCircle2} title="4 · Closed-Loop Care System" tone={EMERALD} highlight>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {LOOPS.map((l, i) => (
              <div key={i} className="avoid-break rounded-xl border-l-4 bg-white/50 p-3" style={{ borderColor: EMERALD }}>
                <p className="font-['Outfit'] font-bold text-sm mb-0.5" style={{ color: EMERALD_DEEP }}>{l.title}</p>
                <p className="text-xs" style={{ color: SLATE }}>{l.body}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Communication principles */}
        <Section icon={MessageSquare} title="5 · Communication Principles" tone={EMERALD}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Principle num="1" title="Clarity" body="Avoid jargon. Use simple explanations." />
            <Principle num="2" title="Timeliness" body="Urgent → immediate. Routine → within expected timeframes." />
            <Principle num="3" title="Documentation" body='"If it is not documented, it did not happen."' />
          </div>
        </Section>

        {/* Safety + Wellbeing + Conduct */}
        <Section icon={ShieldCheck} title="6 · Safety, Wellbeing &amp; Conduct" tone={EMERALD}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-xl border p-4" style={{ borderColor: `${EMERALD}2A` }}>
              <p className="text-[11px] font-black uppercase tracking-[0.15em] mb-2" style={{ color: EMERALD }}>Safety Culture</p>
              <ul className="text-xs space-y-1" style={{ color: SLATE }}>
                <li><strong>Speak-Up Culture</strong> — raise concerns, question unsafe practices, escalate without fear.</li>
                <li><strong>No-Delay Policy</strong> — urgent care is never delayed by workflow, hierarchy, or communication barriers.</li>
                <li><strong>Incident Learning</strong> — all incidents reviewed, documented, used for improvement.</li>
              </ul>
            </div>
            <div className="rounded-xl border p-4" style={{ borderColor: `${EMERALD}2A` }}>
              <p className="text-[11px] font-black uppercase tracking-[0.15em] mb-2" style={{ color: EMERALD }}>Staff Wellbeing</p>
              <p className="text-xs mb-2" style={{ color: SLATE }}>Healthcare work is demanding. Staff under stress cannot deliver optimal care.</p>
              <p className="text-xs" style={{ color: SLATE }}>The practice commits to a <strong>supportive environment</strong>, <strong>fair workload distribution</strong>, and <strong>respectful communication</strong>.</p>
            </div>
            <div className="rounded-xl border p-4 sm:col-span-2" style={{ borderColor: `${EMERALD}2A`, backgroundColor: `${EMERALD}05` }}>
              <p className="text-[11px] font-black uppercase tracking-[0.15em] mb-2" style={{ color: EMERALD }}>Professional Conduct</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs" style={{ color: SLATE }}>
                <div><strong style={{ color: NAVY }}>Respect</strong><p>Zero tolerance for abuse, discrimination, or harassment.</p></div>
                <div><strong style={{ color: NAVY }}>Integrity</strong><p>Honest communication. Ethical decision-making.</p></div>
                <div><strong style={{ color: NAVY }}>Responsibility</strong><p>Own your actions. Escalate when unsure.</p></div>
              </div>
            </div>
          </div>
        </Section>

        {/* Decision framework */}
        <Section icon={ClipboardList} title="7 · Decision-Making Framework" tone={EMERALD} highlight>
          <p className="text-sm mb-3" style={{ color: SLATE }}>When unsure, ask yourself these four questions in order. If any answer is <strong>no</strong>, stop and fix it before proceeding.</p>
          <ol className="space-y-2">
            {DECISION.map((q, i) => (
              <li key={i} className="flex items-start gap-3 rounded-xl border p-3" style={{ borderColor: `${EMERALD}2A` }}>
                <span className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 font-black text-sm" style={{ backgroundColor: EMERALD, color: 'white' }}>{i + 1}</span>
                <span className="text-sm font-medium" style={{ color: NAVY }}>{q}</span>
              </li>
            ))}
          </ol>
        </Section>

        {/* Real-world examples */}
        <Section icon={AlertTriangle} title="8 · Culture in Action" tone={EMERALD}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {EXAMPLES.map((e, i) => (
              <div key={i} className="avoid-break rounded-xl border-l-4 p-4" style={{ borderColor: e.tone, backgroundColor: `${e.tone}08` }}>
                <p className="text-[10px] font-black uppercase tracking-[0.15em] mb-1" style={{ color: e.tone }}>Example {i + 1}</p>
                <p className="font-['Outfit'] font-bold text-sm mb-1" style={{ color: NAVY }}>{e.title}</p>
                <p className="text-xs" style={{ color: SLATE }}>{e.body}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Final statement */}
        <section className="avoid-break rounded-3xl border-2 p-6 sm:p-8 text-center"
          style={{ borderColor: EMERALD, background: `linear-gradient(135deg, ${EMERALD}10, transparent 80%)` }}>
          <p className="text-[10px] font-black uppercase tracking-[0.25em] mb-2" style={{ color: EMERALD }}>Final Statement</p>
          <p className="font-['Outfit'] text-lg sm:text-xl font-bold leading-relaxed max-w-3xl mx-auto" style={{ color: NAVY }}>
            SP Radiology is committed to compassionate, patient-centred care, where every action is guided by
            <span style={{ color: EMERALD }}> safety, communication, </span>
            and
            <span style={{ color: EMERALD }}> complete follow-through</span>.
          </p>
        </section>

        {/* Footer */}
        <div className="mt-8 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
          <p>Culture Charter · SP Radiology (Sanctuary Point Ultrasound &amp; Radiology) · Version 1.0</p>
          <p className="no-print">Owned by Medical Director · Reviewed annually</p>
        </div>
      </main>
    </div>
  );
}

function Section({ icon: Icon, title, tone, children, highlight }) {
  return (
    <section className={`mb-6 rounded-2xl ${highlight ? 'border-2 p-5' : 'p-5 border'}`} style={{ borderColor: highlight ? tone : `${tone}2A`, backgroundColor: highlight ? `${tone}06` : 'transparent' }}>
      <h2 className="flex items-center gap-2 font-['Outfit'] text-xl font-bold mb-4" style={{ color: NAVY }}>
        <Icon className="w-5 h-5" style={{ color: tone }} />
        {title}
      </h2>
      {children}
    </section>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex gap-2"><span style={{ color: EMERALD }}>✓</span><span style={{ color: SLATE }}>{children}</span></li>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold" style={{ color: NAVY }}>{value}</p>
    </div>
  );
}

function Principle({ num, title, body }) {
  return (
    <div className="rounded-xl border p-4" style={{ borderColor: `${EMERALD}2A` }}>
      <div className="flex items-center gap-2 mb-1">
        <span className="w-6 h-6 rounded-full text-xs font-black flex items-center justify-center" style={{ backgroundColor: EMERALD, color: 'white' }}>{num}</span>
        <p className="font-['Outfit'] font-bold text-sm" style={{ color: NAVY }}>{title}</p>
      </div>
      <p className="text-xs" style={{ color: SLATE }}>{body}</p>
    </div>
  );
}
